<script setup>
/**
 * Kanban (custom Portela) — funis de conversas com CRUD.
 *
 * Seletor de funil no topo:
 *  - "Status" (fixo): 4 colunas pelos status nativos (open/pending/snoozed/
 *    resolved); arrastar um card muda o status via toggleStatus.
 *  - Funis custom: cada Atributo Personalizado de CONVERSA do tipo "lista".
 *    Colunas = opções do atributo (+ "Sem etapa"); arrastar grava o valor do
 *    atributo na conversa (updateCustomAttributes, merge pra não apagar outros).
 *
 * CRUD (admin): botão "Gerenciar" abre editor pra criar/renomear/excluir funil e
 * add/renomear/remover/reordenar etapas — persiste no Atributo Personalizado
 * (AttributeAPI), compartilhado com toda a equipe.
 *
 * Reordenar colunas pela alça (grip): funil Status → localStorage (por
 * navegador); funil custom (admin) → grava a ordem em attribute_values.
 *
 * Realtime: escuta 'fetch_conversation_stats' e recarrega em background
 * (debounced). Busca tudo direto na API — não mexe no store da lista.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import ConversationApi from 'dashboard/api/inbox/conversation';
import AttributeAPI from 'dashboard/api/attributes';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useEmitter } from 'dashboard/composables/emitter';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { convertToAttributeSlug } from 'dashboard/helper/commons';

const router = useRouter();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();

const STATUS_FUNNEL_KEY = '__status__';
const NONE_COLUMN_ID = '__none__';
const ATTRIBUTE_MODEL_CONVERSATION = 0; // enum numérico no create
const ATTRIBUTE_TYPE_LIST = 6; // enum numérico no create

const STATUS_COLUMNS = [
  { id: 'open', label: 'Abertas', icon: 'i-lucide-circle-dot', accent: 'text-n-teal-11' },
  { id: 'pending', label: 'Pendentes', icon: 'i-lucide-clock', accent: 'text-n-amber-11' },
  { id: 'snoozed', label: 'Adiadas', icon: 'i-lucide-alarm-clock', accent: 'text-n-blue-11' },
  { id: 'resolved', label: 'Resolvidas', icon: 'i-lucide-check-check', accent: 'text-n-slate-11' },
];

// Paleta de cores da bolinha da coluna (funil custom). Classes literais para o
// Tailwind gerar. A escolha (admin) fica no attribute_description do funil →
// compartilhada com toda a equipe (ver COLOR_MARKER abaixo).
// `dot` (bg) = chip do seletor no editor; `accent` (text) = cor do ícone
// circle-dot na coluna (mesmo formato do "Abertas" do funil Status).
// Paleta ampla. `hex` = cor exata da bolinha (dot/swatch) via style — não depende
// dos tokens do tema (que só têm 7 escalas), então dá pra ter muitas cores.
// `accent` = classe do token EXISTENTE mais próximo (cor do ícone da coluna no board;
// sempre renderiza). As chaves antigas (slate/teal/blue/iris/violet/amber/ruby) são
// MANTIDAS p/ funis já salvos continuarem resolvendo.
const STAGE_COLORS = [
  { key: 'slate', label: 'Cinza', hex: '#8b8d98', accent: 'text-n-slate-11' },
  { key: 'gray', label: 'Grafite', hex: '#6f6f6f', accent: 'text-n-slate-11' },
  { key: 'brown', label: 'Marrom', hex: '#ad7f58', accent: 'text-n-amber-11' },
  { key: 'ruby', label: 'Vermelho', hex: '#e5484d', accent: 'text-n-ruby-11' },
  { key: 'tomato', label: 'Telha', hex: '#e54d2e', accent: 'text-n-ruby-11' },
  { key: 'orange', label: 'Laranja', hex: '#f76b15', accent: 'text-n-amber-11' },
  { key: 'amber', label: 'Âmbar', hex: '#ffb224', accent: 'text-n-amber-11' },
  { key: 'yellow', label: 'Amarelo', hex: '#f5d90a', accent: 'text-n-amber-11' },
  { key: 'lime', label: 'Lima', hex: '#99d52a', accent: 'text-n-teal-11' },
  { key: 'grass', label: 'Verde', hex: '#46a758', accent: 'text-n-teal-11' },
  { key: 'teal', label: 'Verde-água', hex: '#12a594', accent: 'text-n-teal-11' },
  { key: 'cyan', label: 'Ciano', hex: '#00a2c7', accent: 'text-n-blue-11' },
  { key: 'blue', label: 'Azul', hex: '#3e63dd', accent: 'text-n-blue-11' },
  { key: 'indigo', label: 'Anil', hex: '#3a5bc7', accent: 'text-n-blue-11' },
  { key: 'iris', label: 'Índigo', hex: '#5b5bd6', accent: 'text-n-iris-11' },
  { key: 'violet', label: 'Roxo', hex: '#6e56cf', accent: 'text-n-violet-11' },
  { key: 'purple', label: 'Púrpura', hex: '#8e4ec6', accent: 'text-n-violet-11' },
  { key: 'plum', label: 'Ameixa', hex: '#ab4aba', accent: 'text-n-violet-11' },
  { key: 'pink', label: 'Rosa', hex: '#d6409f', accent: 'text-n-ruby-11' },
  { key: 'crimson', label: 'Carmim', hex: '#e93d82', accent: 'text-n-ruby-11' },
];
const DEFAULT_COLOR = 'slate';
const findColor = key => STAGE_COLORS.find(c => c.key === key) || STAGE_COLORS[0];
const dotStyleForColor = key => ({ backgroundColor: findColor(key).hex });
const accentClassForColor = key => findColor(key).accent;

// Cor das etapas fica no `attribute_description` do funil (COMPARTILHADO com a
// equipe): "<nome> ::kbcolors:: {\"etapa\":\"colorKey\"}". O nome continua legível
// na tela nativa; o save nativo preserva a description (não a zera).
const COLOR_MARKER = ' ::kbcolors:: ';
const parseColorsFromDescription = desc => {
  if (!desc) return {};
  const idx = String(desc).indexOf(COLOR_MARKER);
  if (idx === -1) return {};
  try {
    return JSON.parse(String(desc).slice(idx + COLOR_MARKER.length)) || {};
  } catch (e) {
    return {};
  }
};
const buildDescription = (name, colorMap) => {
  const keys = colorMap ? Object.keys(colorMap) : [];
  return keys.length ? `${name}${COLOR_MARKER}${JSON.stringify(colorMap)}` : name;
};

// ---- funis --------------------------------------------------------------
const customAttributes = ref([]); // atributos lista de conversa (crus, snake_case)

const customFunnelList = computed(() =>
  customAttributes.value.map(a => ({
    key: a.attribute_key,
    name: a.attribute_display_name,
    stages: a.attribute_values || [],
    id: a.id,
    description: a.attribute_description,
    colorMap: parseColorsFromDescription(a.attribute_description),
    isStatus: false,
  }))
);

// O funil "Status" só aparece para ADMIN; usuário padrão vê só os funis custom.
const funnels = computed(() =>
  isAdmin.value
    ? [{ key: STATUS_FUNNEL_KEY, name: 'Status', isStatus: true }, ...customFunnelList.value]
    : customFunnelList.value
);

// Fallback seguro se a lista estiver vazia (não-admin sem funil custom) — evita
// currentFunnel undefined nos acessos `.name`/`.isStatus` do header.
const EMPTY_FUNNEL = { key: null, name: '—', isStatus: false, stages: [] };
const selectedFunnelKey = ref(STATUS_FUNNEL_KEY);
const currentFunnel = computed(
  () =>
    funnels.value.find(f => f.key === selectedFunnelKey.value) ||
    funnels.value[0] ||
    EMPTY_FUNNEL
);

// ---- colunas + dados ----------------------------------------------------
const activeColumns = ref([]); // descritores de coluna do funil atual (reordenável)
const boards = ref({}); // { [columnId]: { items: [], loading } }
const savingIds = ref(new Set());

// ---- seleção múltipla + ações em massa ----------------------------------
// selectedIds = Set de display_id (dirige checkbox/contador; reativo por reatribuição).
// selectedConvs = Map id->conversa (fonte de custom_attributes p/ as ações; não-reativo,
// só na lógica). O backend REPLACE o hash inteiro em updateCustomAttributes, então
// precisamos da conversa pra fazer o merge (igual às ações unitárias).
const selectedIds = ref(new Set());
const selectedConvs = new Map();
const bulkRunning = ref(false); // operação em massa em andamento (trava barra + realtime)
const bulkInfo = ref(''); // progresso "X de N"
const bulkMenuOpen = ref(false); // dropdown "Mover para"
const selectAllBusy = ref(null); // colId sendo drenado no "selecionar todos da etapa"

const isSelected = id => selectedIds.value.has(id);
const toggleSelect = conv => {
  const s = new Set(selectedIds.value);
  if (s.has(conv.id)) {
    s.delete(conv.id);
    selectedConvs.delete(conv.id);
  } else {
    s.add(conv.id);
    selectedConvs.set(conv.id, conv);
  }
  selectedIds.value = s;
};
const clearSelection = () => {
  selectedIds.value = new Set();
  selectedConvs.clear();
  bulkMenuOpen.value = false;
};
// Reflete o estado do botão "selecionar todos": true se TODOS os cards já
// carregados da coluna estão marcados (feedback visual do ícone).
const columnAllSelected = colId => {
  const b = boards.value[colId];
  return !!b && b.items.length > 0 && b.items.every(c => isSelected(c.id));
};

// "Selecionar todos da etapa" — TOGGLE: drena TODAS as páginas da coluna (mesmo as
// não carregadas no scroll) e, se TODAS já estão marcadas, DESMARCA; senão marca
// todas. Reusa buildColumnRequest (respeita a janela).
const selectAllInColumn = async col => {
  if (selectAllBusy.value) return;
  selectAllBusy.value = col.id;
  try {
    const req = buildColumnRequest(currentFunnel.value, col);
    const convs = [];
    const SAFETY = 2000;
    for (let page = 1; page <= SAFETY; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await req(page);
      const batch = extractPayload(res);
      convs.push(...batch);
      if (batch.length < PER_PAGE) break; // página curta = fim
    }
    if (!convs.length) return;
    // Toggle: se todos os da etapa já estão selecionados, desmarca; senão marca.
    const allSelected = convs.every(c => selectedIds.value.has(c.id));
    const s = new Set(selectedIds.value);
    convs.forEach(c => {
      if (allSelected) {
        s.delete(c.id);
        selectedConvs.delete(c.id);
      } else {
        s.add(c.id);
        selectedConvs.set(c.id, c);
      }
    });
    selectedIds.value = s;
  } finally {
    selectAllBusy.value = null;
  }
};

// Overflow por coluna: o padding direito só encolhe (pra colar na scrollbar)
// QUANDO a coluna tem barra de rolagem; sem barra, mantém o padding simétrico.
// Diretiva v-col-scroll mede scrollHeight>clientHeight pós-render + no resize.
const columnOverflow = ref({});
const columnEls = {};
const measureColumnOverflow = colId => {
  const el = columnEls[colId];
  if (!el) return;
  const ov = el.scrollHeight > el.clientHeight + 1;
  if (columnOverflow.value[colId] !== ov) {
    columnOverflow.value = { ...columnOverflow.value, [colId]: ov };
  }
};
const measureAllColumns = () =>
  Object.keys(columnEls).forEach(measureColumnOverflow);
const vColScroll = {
  mounted: (el, b) => {
    columnEls[b.value] = el;
    measureColumnOverflow(b.value);
  },
  updated: (el, b) => {
    columnEls[b.value] = el;
    measureColumnOverflow(b.value);
  },
  unmounted: (el, b) => {
    delete columnEls[b.value];
  },
};

const orderStorageKey = funnelKey => `kanban_order_${funnelKey}`;

const loadSavedOrder = funnelKey => {
  try {
    const raw = localStorage.getItem(orderStorageKey(funnelKey));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const saveLocalOrder = () => {
  try {
    localStorage.setItem(
      orderStorageKey(selectedFunnelKey.value),
      JSON.stringify(activeColumns.value.map(c => c.id))
    );
  } catch (e) {
    /* localStorage indisponível: ordem só nesta sessão */
  }
};

const buildColumns = funnel => {
  let cols;
  if (funnel.isStatus) {
    cols = STATUS_COLUMNS.map(c => ({ ...c }));
    const order = loadSavedOrder(funnel.key);
    if (order && order.length) {
      cols.sort((a, b) => {
        const ia = order.indexOf(a.id);
        const ib = order.indexOf(b.id);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
    }
  } else {
    // Funil custom: ordem = ordem do attribute_values (compartilhada).
    const colorMap = funnel.colorMap || {};
    // A etapa "admin" (leads atribuídos ao admin) só aparece para ADMIN;
    // usuário comum não vê essa coluna.
    cols = (funnel.stages || [])
      .filter(
        stage =>
          isAdmin.value || String(stage).trim().toLowerCase() !== 'admin'
      )
      .map(stage => ({
        id: `s:${stage}`,
        label: stage,
        stage,
        icon: 'i-lucide-circle-dot',
        accent: accentClassForColor(colorMap[stage] || DEFAULT_COLOR),
      }));
    const noneCol = {
      id: NONE_COLUMN_ID,
      label: 'Aberto',
      none: true,
      icon: 'i-lucide-circle-dot',
      accent: 'text-n-teal-11',
    };
    // "Aberto" (sem etapa) SEMPRE na primeira posição (à esquerda), universal —
    // ignora qualquer preferência de posição salva.
    cols.unshift(noneCol);
  }
  return cols;
};

const extractPayload = res => {
  const body = res?.data?.data || res?.data || {};
  return body.payload || [];
};

// Janela "recente" do board: só conversas com atividade nos últimos N dias, pra
// não ficar pesado. TODAS as colunas usam POST /conversations/filter com a
// condição `last_activity_at is_greater_than <dataISO(hoje-Nd)>` encadeada (AND)
// à condição primária (status / etapa / sem-etapa). NÃO se aplica à migração.
// N é configurável na UI (30/60/90), persistido por navegador.
const WINDOW_OPTIONS = [30, 60, 90];
const DEFAULT_WINDOW_DAYS = 30;
const windowDays = ref(DEFAULT_WINDOW_DAYS);
const recentSinceIso = () =>
  new Date(Date.now() - windowDays.value * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

// Tamanho da página do backend (Kaminari). O board carrega 1 página por vez e vai
// puxando as próximas ao rolar até o fim da coluna (scroll infinito, igual inbox).
const PER_PAGE = 25;

// Contagem total (dentro da janela) que o backend devolve em meta.all_count.
const extractMetaCount = res => {
  const body = res?.data?.data || res?.data || {};
  const meta = body.meta || {};
  return meta.all_count ?? meta.count ?? 0;
};

const dedupeById = list => {
  const seen = new Set();
  return list.filter(c => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
};

// Chaves de TODOS os funis custom (exclui o funil Status, que pode coexistir).
// Base da regra de exclusividade: um lead pertence a UM funil custom por vez.
const customFunnelKeys = () =>
  funnels.value.filter(f => !f.isStatus).map(f => f.key);

// A etapa "admin" é uma ETAPA NORMAL (atributo == 'admin'): quem é atribuído a um
// admin recebe essa etapa automaticamente pelo backend (initializer
// z_kanban_admin_stage.rb) → cai nesta coluna e sai das outras (exclusividade).

// Monta a função `page => Promise<res>` da coluna (status / etapa / sem-etapa),
// sempre com a janela recente (last_activity_at) encadeada em AND.
const buildColumnRequest = (funnel, column) => {
  const recentCondition = {
    attribute_key: 'last_activity_at',
    filter_operator: 'is_greater_than',
    values: [recentSinceIso()],
    query_operator: null,
  };
  const payload = [];
  if (funnel.isStatus) {
    // Via /filter (pagina 25/página) — `get()+updated_within` desativa a
    // paginação no ConversationFinder e dá 500 em status grandes (ex. resolved).
    payload.push({ attribute_key: 'status', filter_operator: 'equal_to', values: [column.id], query_operator: 'and' });
  } else if (column.none) {
    // Coluna "Aberto": só ABERTAS e que NÃO pertencem a NENHUM funil custom
    // (exclusividade — se já tem etapa em qualquer funil, não duplica aqui).
    // Tickets finalizados (resolved/etc) também ficam de fora.
    payload.push({ attribute_key: 'status', filter_operator: 'equal_to', values: ['open'], query_operator: 'and' });
    customFunnelKeys().forEach(k => {
      payload.push({ attribute_key: k, filter_operator: 'is_not_present', values: [], query_operator: 'and' });
    });
  } else {
    payload.push({ attribute_key: funnel.key, filter_operator: 'equal_to', values: [column.stage], query_operator: 'and' });
  }
  payload.push(recentCondition);
  return page =>
    ConversationApi.filter({
      queryData: { payload },
      page,
    });
};

// Carga inicial / reset da coluna. No refetch de realtime (background) recarrega
// TODAS as páginas que já estavam à mostra (board.page), pra não colapsar o que o
// usuário já rolou. Página 1 na carga normal.
const fetchColumn = async (funnel, column, { background = false } = {}) => {
  const board = boards.value[column.id];
  if (!board) return;
  if (!background) board.loading = true;
  try {
    const req = buildColumnRequest(funnel, column);
    const pagesToLoad = background ? Math.max(1, board.page || 1) : 1;
    const collected = [];
    let total = 0;
    let loadedPages = 0;
    let lastLen = 0;
    for (let p = 1; p <= pagesToLoad; p += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await req(p);
      const list = extractPayload(res);
      total = extractMetaCount(res) || total;
      collected.push(...list);
      loadedPages = p;
      lastLen = list.length;
      if (list.length < PER_PAGE) break;
    }
    board.items = dedupeById(collected);
    board.page = loadedPages;
    board.total = total;
    // Fim = última página CURTA (< 25). NÃO gatear no `total` (all_count desliza
    // com a janela → items.length nunca alcança e o scroll pediria páginas vazias
    // pra sempre). `total` é só pro badge.
    board.hasMore = lastLen >= PER_PAGE;
  } catch (error) {
    if (!background) {
      board.items = [];
      board.hasMore = false;
    }
  } finally {
    if (boards.value[column.id]) boards.value[column.id].loading = false;
  }
};

// Carrega a próxima página da coluna e ANEXA (scroll infinito). Guardas evitam
// disparo duplo enquanto uma página está em voo ou quando não há mais.
const loadMoreColumn = async (funnel, column) => {
  const board = boards.value[column.id];
  if (!board || board.loading || board.loadingMore || !board.hasMore) return;
  board.loadingMore = true;
  try {
    const req = buildColumnRequest(funnel, column);
    const next = (board.page || 1) + 1;
    const res = await req(next);
    const list = extractPayload(res);
    const total = extractMetaCount(res);
    const existing = new Set(board.items.map(c => c.id));
    board.items.push(...list.filter(c => !existing.has(c.id)));
    board.page = next;
    if (total) board.total = total;
    // Fim = página CURTA (< 25). Sinal confiável mesmo com `total` deslizando.
    board.hasMore = list.length >= PER_PAGE;
  } catch (error) {
    board.hasMore = false;
  } finally {
    if (boards.value[column.id]) boards.value[column.id].loadingMore = false;
  }
};

// Scroll infinito: dispara quando a rolagem da coluna chega a <=250px do fim.
// Preso ao scroll REAL do container (não ao viewport), então só carrega quando o
// usuário rola de fato; loadMoreColumn tem as guardas (loading/loadingMore/hasMore).
const onColumnScroll = (column, evt) => {
  const el = evt.currentTarget;
  if (el.scrollHeight - el.scrollTop - el.clientHeight <= 250) {
    loadMoreColumn(currentFunnel.value, column);
  }
};

const fetchAll = (opts = {}) => {
  const funnel = currentFunnel.value;
  return Promise.all(activeColumns.value.map(col => fetchColumn(funnel, col, opts)));
};

const loadFunnel = () => {
  clearSelection(); // troca de funil zera a seleção múltipla
  const funnel = currentFunnel.value;
  // Sem funil válido (não-admin sem funil custom): board vazio, nada a buscar.
  if (!funnel || funnel.key === null) {
    activeColumns.value = [];
    boards.value = {};
    return;
  }
  const cols = buildColumns(funnel);
  const nextBoards = {};
  cols.forEach(c => {
    nextBoards[c.id] = {
      items: [],
      loading: true,
      loadingMore: false,
      page: 0,
      hasMore: true,
      total: 0,
    };
  });
  boards.value = nextBoards;
  activeColumns.value = cols;
  fetchAll();
};

// ---- realtime -----------------------------------------------------------
let refetchTimer = null;
const isRefreshing = ref(false);

const scheduleRefetch = () => {
  if (refetchTimer) clearTimeout(refetchTimer);
  refetchTimer = setTimeout(async () => {
    refetchTimer = null;
    if (savingIds.value.size > 0) {
      scheduleRefetch();
      return;
    }
    isRefreshing.value = true;
    try {
      await fetchAll({ background: true });
    } finally {
      isRefreshing.value = false;
    }
    checkAdminAssignments();
  }, 1200);
};

useEmitter('fetch_conversation_stats', scheduleRefetch);

// ---- notificação de lead atribuído ao admin (popup + som) ---------------
// SÓ admin. A cada realtime/abertura busca "atribuídas a mim + abertas"; IDs
// novos (ainda não reconhecidos) abrem um modal bloqueante e tocam som. O
// conjunto "já visto" persiste em localStorage → se o admin estava off, ao
// ABRIR o Kanban os pendentes disparam o popup. Escopo: só com o Kanban aberto.
const ASSIGN_SEEN_KEY = 'kanban_admin_assigned_seen';
const assignPopup = ref({ open: false, leads: [] });
let assignSeen; // undefined = não carregado; null = 1ª vez (semear baseline)
let assignAudio = null;
let assignPollTimer = null;

const readAssignSeen = () => {
  try {
    const raw = localStorage.getItem(ASSIGN_SEEN_KEY);
    return raw === null ? null : new Set(JSON.parse(raw));
  } catch (e) {
    return null;
  }
};
const writeAssignSeen = set => {
  try {
    localStorage.setItem(ASSIGN_SEEN_KEY, JSON.stringify([...set]));
  } catch (e) {
    /* localStorage indisponível */
  }
};

const playAssignSound = () => {
  try {
    if (!assignAudio) assignAudio = new Audio('/audio/dashboard/ping.mp3');
    assignAudio.currentTime = 0;
    // Navegador pode bloquear autoplay até haver interação → falha silenciosa.
    const p = assignAudio.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) {
    /* sem áudio */
  }
};

const checkAdminAssignments = async () => {
  if (!isAdmin.value) return;
  let leads;
  try {
    const res = await ConversationApi.get({
      assigneeType: 'me',
      status: 'open',
      page: 1,
    });
    leads = extractPayload(res);
  } catch (e) {
    return;
  }
  const current = new Set(leads.map(c => c.id));
  if (assignSeen === undefined) assignSeen = readAssignSeen();
  if (assignSeen === null) {
    // 1ª vez neste navegador: vira baseline SEM popup (evita avalanche no 1º load).
    assignSeen = current;
    writeAssignSeen(assignSeen);
    return;
  }
  const fresh = leads.filter(c => !assignSeen.has(c.id));
  // seen = current: resolvidas saem (podem re-notificar se voltarem a ser atribuídas).
  assignSeen = current;
  writeAssignSeen(assignSeen);
  if (fresh.length) {
    assignPopup.value = {
      open: true,
      leads: fresh.map(c => ({ id: c.id, name: senderName(c) })),
    };
    playAssignSound();
  }
};

const openAssignedLead = lead => {
  assignPopup.value = { open: false, leads: [] };
  openConversation({ id: lead.id });
};
const dismissAssignPopup = () => {
  assignPopup.value = { open: false, leads: [] };
};

// ---- boot ---------------------------------------------------------------
const fetchFunnels = async () => {
  try {
    const res = await AttributeAPI.getAttributesByModel();
    customAttributes.value = (res.data || []).filter(
      a => a.attribute_model === 'conversation_attribute' && a.attribute_display_type === 'list'
    );
  } catch (error) {
    customAttributes.value = [];
  }
};

onMounted(async () => {
  try {
    const saved = localStorage.getItem('kanban_selected_funnel');
    if (saved) selectedFunnelKey.value = saved;
    const savedWindow = parseInt(localStorage.getItem('kanban_window_days'), 10);
    if (WINDOW_OPTIONS.includes(savedWindow)) windowDays.value = savedWindow;
  } catch (e) {
    /* ignore */
  }
  await fetchFunnels();
  // Se o funil salvo não existe p/ este usuário (ex.: não-admin com 'Status'
  // salvo, que agora está oculto), cai no primeiro funil disponível.
  if (!funnels.value.some(f => f.key === selectedFunnelKey.value)) {
    selectedFunnelKey.value = funnels.value.length
      ? funnels.value[0].key
      : STATUS_FUNNEL_KEY;
  }
  loadFunnel();
  window.addEventListener('resize', measureAllColumns);
  // Notificação de leads atribuídos ao admin: checa ao abrir (pega pendentes de
  // quando estava off) + poll de segurança caso os eventos de realtime rareiem.
  checkAdminAssignments();
  assignPollTimer = setInterval(checkAdminAssignments, 20000);
});

onBeforeUnmount(() => {
  if (refetchTimer) clearTimeout(refetchTimer);
  if (assignPollTimer) clearInterval(assignPollTimer);
  window.removeEventListener('resize', measureAllColumns);
});

watch(selectedFunnelKey, key => {
  try {
    localStorage.setItem('kanban_selected_funnel', key);
  } catch (e) {
    /* ignore */
  }
  loadFunnel();
});

// Trocar a janela (30/60/90) → persiste e refaz os fetches das colunas atuais.
watch(windowDays, days => {
  try {
    localStorage.setItem('kanban_window_days', String(days));
  } catch (e) {
    /* ignore */
  }
  fetchAll();
});

// ---- dropdowns custom do header (funil + janela) ------------------------
const funnelMenuOpen = ref(false);
const windowMenuOpen = ref(false);
const toggleFunnelMenu = () => {
  windowMenuOpen.value = false;
  funnelMenuOpen.value = !funnelMenuOpen.value;
};
const toggleWindowMenu = () => {
  funnelMenuOpen.value = false;
  windowMenuOpen.value = !windowMenuOpen.value;
};
const selectFunnel = key => {
  funnelMenuOpen.value = false;
  if (key !== selectedFunnelKey.value) selectedFunnelKey.value = key; // watch → loadFunnel
};
const selectWindow = d => {
  windowMenuOpen.value = false;
  if (d !== windowDays.value) windowDays.value = d; // watch → fetchAll
};

// ---- drag: cards --------------------------------------------------------
// Aplica o "drop numa coluna" a UMA conversa — lógica única, reusada pelo drag
// simples, pelo arrastar-em-grupo e pelo "Mover para" da barra de ações.
const applyCardToColumn = async (conversation, column, funnel) => {
  if (funnel.isStatus) {
    if (conversation.status === column.id) return;
    await ConversationApi.toggleStatus({
      conversationId: conversation.id,
      status: column.id,
      snoozedUntil: null,
    });
    conversation.status = column.id;
    return;
  }
  const merged = { ...(conversation.custom_attributes || {}) };
  if (column.none) {
    // "Aberto" = não pertence a NENHUM funil custom: limpa a etapa de TODOS
    // e reabre o ticket (a coluna só lista abertos sem etapa).
    customFunnelKeys().forEach(k => delete merged[k]);
    if (conversation.status !== 'open') {
      await ConversationApi.toggleStatus({
        conversationId: conversation.id,
        status: 'open',
        snoozedUntil: null,
      });
      conversation.status = 'open';
    }
  } else {
    // Exclusividade: ao entrar numa etapa deste funil, sai de todos os outros
    // funis custom (um lead pertence a um funil só).
    merged[funnel.key] = column.stage;
    customFunnelKeys().forEach(k => {
      if (k !== funnel.key) delete merged[k];
    });
  }
  await ConversationApi.updateCustomAttributes({
    conversationId: conversation.id,
    customAttributes: merged,
  });
  conversation.custom_attributes = merged;
};

const onCardChange = async (column, evt) => {
  if (!evt.added) return;
  const funnel = currentFunnel.value;
  const dragged = evt.added.element;
  // Arrastar-em-grupo: se o card arrastado faz parte de uma seleção múltipla,
  // o drop move TODA a seleção (visual arrasta 1, resultado move o grupo).
  const group =
    selectedIds.value.has(dragged.id) && selectedIds.value.size > 1
      ? [...selectedConvs.values()]
      : [dragged];
  const isBulk = group.length > 1;
  if (isBulk) bulkRunning.value = true;
  let done = 0;
  for (const conversation of group) {
    if (isBulk) bulkInfo.value = `${done + 1} de ${group.length}`;
    savingIds.value.add(conversation.id);
    try {
      // eslint-disable-next-line no-await-in-loop
      await applyCardToColumn(conversation, column, funnel);
      done += 1;
    } catch (error) {
      // eslint-disable-next-line no-await-in-loop
      await fetchAll({ background: true });
    } finally {
      savingIds.value.delete(conversation.id);
    }
  }
  if (isBulk) {
    bulkRunning.value = false;
    bulkInfo.value = '';
    clearSelection();
    // os outros cards moveram via API (não por DnD) → re-sincroniza os boards.
    await fetchAll({ background: true });
  }
};

// ---- menu do card (⋮): remover do funil ---------------------------------
// Menu POSICIONADO NO CLIQUE (fixed) — o dropdown absoluto seria recortado pelo
// container overflow-y-auto da coluna. `cardMenu` = { conversation, x, y } | null.
const cardMenu = ref(null);
const openCardMenu = (conversation, evt) => {
  const x = Math.max(8, evt.clientX - 190); // abre p/ a esquerda do clique
  const y = Math.min(evt.clientY, window.innerHeight - 60);
  cardMenu.value = { conversation, x, y };
};

const removeCardFromBoards = id => {
  Object.values(boards.value).forEach(board => {
    const i = board.items.findIndex(c => c.id === id);
    if (i !== -1) {
      board.items.splice(i, 1);
      if (board.total > 0) board.total -= 1;
    }
  });
};

// "Remover do funil": limpa a etapa (se houver) E finaliza (resolve). Lógica
// única, reusada pelo ⋮ do card e pela ação em massa.
const resolveAndClear = async (conversation, funnel) => {
  if (!funnel.isStatus) {
    const merged = { ...(conversation.custom_attributes || {}) };
    if (funnel.key in merged) {
      delete merged[funnel.key];
      await ConversationApi.updateCustomAttributes({
        conversationId: conversation.id,
        customAttributes: merged,
      });
      conversation.custom_attributes = merged;
    }
  }
  if (conversation.status !== 'resolved') {
    await ConversationApi.toggleStatus({
      conversationId: conversation.id,
      status: 'resolved',
      snoozedUntil: null,
    });
    conversation.status = 'resolved';
  }
};

// ⋮ do card → o card sai do quadro sempre, mesmo se estava aberto.
const removeFromFunnel = async conversation => {
  cardMenu.value = null;
  const funnel = currentFunnel.value;
  savingIds.value.add(conversation.id);
  try {
    await resolveAndClear(conversation, funnel);
    removeCardFromBoards(conversation.id);
  } catch (error) {
    await fetchAll({ background: true });
  } finally {
    savingIds.value.delete(conversation.id);
  }
};

// ---- executor das ações em massa ----------------------------------------
// Loop sequencial (igual à migração) sobre a seleção, com progresso, savingIds
// por card (trava o refetch de realtime) e re-sync no fim. `fn(conv)` é a ação.
const runBulk = async fn => {
  const convs = [...selectedConvs.values()];
  if (!convs.length || bulkRunning.value) return;
  bulkRunning.value = true;
  let done = 0;
  for (const conv of convs) {
    bulkInfo.value = `${done + 1} de ${convs.length}`;
    savingIds.value.add(conv.id);
    try {
      // eslint-disable-next-line no-await-in-loop
      await fn(conv);
      done += 1;
    } catch (error) {
      /* pula o que falhar; o re-sync no fim reflete o estado real */
    } finally {
      savingIds.value.delete(conv.id);
    }
  }
  bulkRunning.value = false;
  bulkInfo.value = '';
  clearSelection();
  await fetchAll({ background: true });
};

// Barra: "Mover para" uma coluna/etapa.
const bulkMoveToStage = col => {
  bulkMenuOpen.value = false;
  const funnel = currentFunnel.value;
  return runBulk(conv => applyCardToColumn(conv, col, funnel));
};
// Barra: "Remover/Finalizar" todos os selecionados.
const bulkRemove = () => {
  const funnel = currentFunnel.value;
  return runBulk(conv => resolveAndClear(conv, funnel));
};

// ---- drag: colunas ------------------------------------------------------
const canDragColumn = col => {
  // "Aberto" (sem etapa) é FIXA à esquerda: nunca arrastável (sem grip).
  if (col.none) return false;
  if (currentFunnel.value.isStatus) return true;
  return isAdmin.value;
};

// Trava a "Aberto" (sem etapa) na 1ª posição: bloqueia qualquer movimento que
// a arraste OU que tente assumir a esquerda na frente dela.
const onColumnMove = evt => {
  if (evt.draggedContext?.element?.none) return false;
  if (evt.relatedContext?.element?.none) return false;
  return true;
};

const onColumnReorder = async () => {
  const funnel = currentFunnel.value;
  if (funnel.isStatus) {
    saveLocalOrder();
    return;
  }
  if (!isAdmin.value) return;
  const stages = activeColumns.value.filter(c => !c.none).map(c => c.stage);
  try {
    await AttributeAPI.update(funnel.id, {
      attribute_display_name: funnel.name,
      attribute_description: buildDescription(funnel.name, funnel.colorMap),
      attribute_values: stages,
    });
    await fetchFunnels(); // atualiza a fonte sem rebuildar o board (sem flicker)
  } catch (error) {
    await fetchFunnels();
    loadFunnel();
  }
};

// ---- editor de funil (CRUD, admin) --------------------------------------
const showEditor = ref(false);
const editorMode = ref('edit'); // 'edit' | 'create'
const editorName = ref('');
const editorStages = ref([]); // array de { id, value }
const editorNewStage = ref('');
const editorError = ref('');
const editorSaving = ref(false);
const migrationInfo = ref(''); // texto de progresso da migração
const originalStagesById = ref({}); // { [rowId]: valorOriginal } — p/ detectar rename/remove
let stageIdSeq = 0;

const toStageRows = (values, colorMap = {}) =>
  (values || []).map(v => ({
    id: (stageIdSeq += 1),
    value: v,
    color: colorMap[v] || DEFAULT_COLOR,
  }));

// { valor: colorKey } alinhado com editorStageValues (1ª ocorrência)
const editorColorMap = () => {
  const seen = new Set();
  const map = {};
  editorStages.value.forEach(s => {
    const v = (s.value || '').trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      map[v] = s.color || DEFAULT_COLOR;
    }
  });
  return map;
};

const colorPickerRowId = ref(null);
const toggleColorPicker = id => {
  colorPickerRowId.value = colorPickerRowId.value === id ? null : id;
};
const pickColor = (stage, key) => {
  stage.color = key;
  colorPickerRowId.value = null;
};

// nomes de etapa finais (trim, sem vazias, sem duplicadas)
const editorStageValues = () => {
  const seen = new Set();
  const out = [];
  editorStages.value.forEach(s => {
    const v = (s.value || '').trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  });
  return out;
};

const openCreate = () => {
  editorMode.value = 'create';
  editorName.value = '';
  editorStages.value = [];
  editorNewStage.value = '';
  editorError.value = '';
  showEditor.value = true;
};

const openEdit = () => {
  const funnel = currentFunnel.value;
  if (funnel.isStatus) {
    openCreate();
    return;
  }
  editorMode.value = 'edit';
  editorName.value = funnel.name;
  const rows = toStageRows(funnel.stages, funnel.colorMap || {});
  editorStages.value = rows;
  originalStagesById.value = Object.fromEntries(rows.map(r => [r.id, r.value]));
  editorNewStage.value = '';
  editorError.value = '';
  migrationInfo.value = '';
  colorPickerRowId.value = null;
  showEditor.value = true;
};

const closeEditor = () => {
  showEditor.value = false;
};

const addEditorStage = () => {
  const v = editorNewStage.value.trim();
  if (!v) return;
  if (editorStages.value.some(s => (s.value || '').trim() === v)) {
    editorError.value = `A etapa "${v}" já existe.`;
    return;
  }
  editorStages.value.push({ id: (stageIdSeq += 1), value: v, color: DEFAULT_COLOR });
  editorNewStage.value = '';
  editorError.value = '';
};

const removeEditorStage = index => {
  editorStages.value.splice(index, 1);
};

// Busca a 1ª página (25) de conversas cujo atributo `funnelKey` == `value`.
const fetchStagePage = (funnelKey, value) =>
  ConversationApi.filter({
    queryData: {
      payload: [
        { attribute_key: funnelKey, filter_operator: 'equal_to', values: [value], query_operator: null },
      ],
    },
    page: 1,
  });

// Move as conversas do valor `fromValue` para `toValue` (null = limpa a etapa).
// "Drena" em lotes de 25: como migrar remove a conversa do filtro `equal_to
// fromValue`, a página 1 sempre traz o próximo lote — assim NÃO há teto de ~1000
// (a abordagem antiga de buscar tudo travava em ~1000 e truncava etapas grandes
// silenciosamente). `failedIds` evita reprocessar/loopar em conversas que falham.
// Retorna { moved, failed }.
const migrateStageValue = async (funnelKey, fromValue, toValue, progressLabel) => {
  let moved = 0;
  let failed = 0;
  const failedIds = new Set();
  const SAFETY_ITERATIONS = 2000; // trava dura contra loop infinito (~50k conversas)
  for (let iter = 0; iter < SAFETY_ITERATIONS; iter += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchStagePage(funnelKey, fromValue);
    const batch = extractPayload(res).filter(c => !failedIds.has(c.id));
    if (!batch.length) break; // acabou (ou só sobraram conversas que falham)
    for (let i = 0; i < batch.length; i += 1) {
      const conv = batch[i];
      migrationInfo.value = `${progressLabel} (${moved + 1})`;
      const merged = { ...(conv.custom_attributes || {}) };
      if (toValue === null) delete merged[funnelKey];
      else merged[funnelKey] = toValue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await ConversationApi.updateCustomAttributes({
          conversationId: conv.id,
          customAttributes: merged,
        });
        moved += 1;
      } catch (e) {
        failed += 1;
        failedIds.add(conv.id);
      }
    }
  }
  return { moved, failed };
};

// Compara o estado do editor com o snapshot original e roda as migrações.
const runStageMigrations = async funnel => {
  const renames = []; // { from, to }
  editorStages.value.forEach(row => {
    const original = originalStagesById.value[row.id];
    const current = (row.value || '').trim();
    if (original !== undefined && current && original !== current) {
      renames.push({ from: original, to: current });
    }
  });

  const currentIds = new Set(editorStages.value.map(r => r.id));
  const removed = Object.entries(originalStagesById.value)
    .filter(([id]) => !currentIds.has(Number(id)))
    .map(([, value]) => value)
    .filter(Boolean);

  let totalFailed = 0;
  for (let i = 0; i < renames.length; i += 1) {
    const { from, to } = renames[i];
    // eslint-disable-next-line no-await-in-loop
    const r = await migrateStageValue(funnel.key, from, to, `Movendo "${from}" → "${to}"`);
    totalFailed += r.failed;
  }
  for (let i = 0; i < removed.length; i += 1) {
    const from = removed[i];
    // eslint-disable-next-line no-await-in-loop
    const r = await migrateStageValue(funnel.key, from, null, `Limpando "${from}"`);
    totalFailed += r.failed;
  }
  return totalFailed;
};

const saveCreate = async () => {
  const name = editorName.value.trim();
  const stages = editorStageValues();
  if (!name) {
    editorError.value = 'Dê um nome ao funil.';
    return;
  }
  if (!stages.length) {
    editorError.value = 'Adicione pelo menos uma etapa.';
    return;
  }
  editorSaving.value = true;
  editorError.value = '';
  try {
    const key = convertToAttributeSlug(name);
    await AttributeAPI.create({
      attribute_display_name: name,
      attribute_description: buildDescription(name, editorColorMap()),
      attribute_model: ATTRIBUTE_MODEL_CONVERSATION,
      attribute_display_type: ATTRIBUTE_TYPE_LIST,
      attribute_key: key,
      attribute_values: stages,
      regex_pattern: null,
      regex_cue: null,
    });
    await fetchFunnels();
    showEditor.value = false;
    selectedFunnelKey.value = key; // watch dispara loadFunnel
  } catch (error) {
    editorError.value =
      error?.response?.data?.message ||
      'Não foi possível criar o funil (a chave pode já existir).';
  } finally {
    editorSaving.value = false;
  }
};

const saveEdit = async () => {
  const funnel = currentFunnel.value;
  const name = editorName.value.trim();
  const stages = editorStageValues();
  if (!name) {
    editorError.value = 'O funil precisa de um nome.';
    return;
  }
  if (!stages.length) {
    editorError.value = 'Mantenha pelo menos uma etapa.';
    return;
  }
  editorSaving.value = true;
  editorError.value = '';
  migrationInfo.value = '';
  try {
    await AttributeAPI.update(funnel.id, {
      attribute_display_name: name,
      attribute_description: buildDescription(name, editorColorMap()),
      attribute_values: stages,
    });
    // Migra conversas de etapas renomeadas/removidas (automático).
    const failed = await runStageMigrations(funnel);
    migrationInfo.value = '';
    await fetchFunnels();
    if (failed > 0) {
      editorError.value = `Funil salvo, mas ${failed} conversa(s) não puderam ser migradas.`;
    } else {
      showEditor.value = false;
    }
    loadFunnel(); // etapas podem ter mudado → rebuilda colunas
  } catch (error) {
    migrationInfo.value = '';
    editorError.value =
      error?.response?.data?.message || 'Não foi possível salvar o funil.';
  } finally {
    editorSaving.value = false;
  }
};

const deleteFunnel = async () => {
  const funnel = currentFunnel.value;
  if (funnel.isStatus) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir o funil "${funnel.name}"? As conversas não são apagadas, só perdem a marcação de etapa deste funil.`)) {
    return;
  }
  editorSaving.value = true;
  try {
    await AttributeAPI.delete(funnel.id);
    await fetchFunnels();
    showEditor.value = false;
    selectedFunnelKey.value = STATUS_FUNNEL_KEY;
  } catch (error) {
    editorError.value =
      error?.response?.data?.message || 'Não foi possível excluir o funil.';
  } finally {
    editorSaving.value = false;
  }
};

// ---- helpers de card ----------------------------------------------------
const senderName = c => c?.meta?.sender?.name || 'Sem nome';
const senderThumbnail = c => c?.meta?.sender?.thumbnail || '';

// Contador do cabeçalho da coluna = total real da janela (meta.all_count); cai no
// nº de itens carregados enquanto o total não veio. Scroll infinito puxa o resto.
const columnCountLabel = colId => {
  const board = boards.value[colId];
  if (!board) return '0';
  return `${board.total || board.items.length}`;
};

const lastMessageText = c => {
  const messages = c?.messages || [];
  const last = messages[messages.length - 1];
  if (!last) return '';
  if (last.content) return last.content;
  if (last.attachments && last.attachments.length) return '📎 Anexo';
  return '';
};

const formatTimestamp = c => {
  const ts = c?.timestamp || c?.last_activity_at;
  if (!ts) return '';
  const date = new Date(ts * 1000);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const openConversation = c => {
  router.push(frontendURL(conversationUrl({ accountId: accountId.value, id: c.id })));
};
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header
      class="flex flex-wrap items-center justify-between flex-shrink-0 gap-2 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex items-center gap-2">
          <span class="i-lucide-trello text-n-slate-12 size-5" />
          <h1 class="text-lg font-medium text-n-slate-12">Kanban</h1>
        </div>

        <span
          class="flex items-center gap-1 text-xs text-n-slate-10"
          :title="isRefreshing ? 'Atualizando…' : 'Atualiza em tempo real'"
        >
          <span
            class="rounded-full size-2"
            :class="isRefreshing ? 'bg-n-amber-9 animate-pulse' : 'bg-n-teal-9'"
          />
          ao vivo
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          class="flex items-center justify-center flex-shrink-0 !size-9 rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
          title="Atualizar"
          @click="fetchAll()"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="{ 'animate-spin': isRefreshing }"
          />
        </button>

        <template v-if="isAdmin">
          <button
            v-if="!currentFunnel.isStatus"
            class="flex items-center gap-1.5 !h-9 px-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
            @click="openEdit"
          >
            <span class="i-lucide-settings-2 size-4" />
            Gerenciar
          </button>
          <button
            class="flex items-center gap-1.5 !h-9 px-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
            @click="openCreate"
          >
            <span class="i-lucide-plus size-4" />
            Novo
          </button>
        </template>

        <!-- Dropdown FUNIL (custom: botão + menu de divs; sem <select> nativo) -->
        <div class="relative">
          <button
            class="flex items-center gap-1.5 !h-9 px-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
            title="Escolher funil"
            @click="toggleFunnelMenu"
          >
            <span class="truncate max-w-[10rem]">{{ currentFunnel.name }}</span>
            <span class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10" />
          </button>
          <template v-if="funnelMenuOpen">
            <div class="fixed inset-0 z-40" @click="funnelMenuOpen = false" />
            <div
              class="absolute right-0 z-50 py-1 mt-1 overflow-y-auto border shadow-md rounded-lg top-full min-w-[12rem] max-h-72 bg-n-background border-n-weak"
            >
              <button
                v-for="f in funnels"
                :key="f.key"
                class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left hover:bg-n-alpha-2"
                :class="f.key === selectedFunnelKey ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'"
                @click="selectFunnel(f.key)"
              >
                <span
                  class="i-lucide-check size-4 flex-shrink-0"
                  :class="f.key === selectedFunnelKey ? 'text-n-slate-12' : 'opacity-0'"
                />
                <span class="truncate">{{ f.name }}</span>
              </button>
            </div>
          </template>
        </div>

        <!-- Dropdown JANELA 30/60/90 (custom) -->
        <div class="relative">
          <button
            class="flex items-center gap-1.5 !h-9 px-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
            title="Período: só conversas com atividade nos últimos N dias"
            @click="toggleWindowMenu"
          >
            <span class="whitespace-nowrap">Últimos {{ windowDays }} dias</span>
            <span class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10" />
          </button>
          <template v-if="windowMenuOpen">
            <div class="fixed inset-0 z-40" @click="windowMenuOpen = false" />
            <div
              class="absolute right-0 z-50 py-1 mt-1 border shadow-md rounded-lg top-full min-w-[11rem] bg-n-background border-n-weak"
            >
              <button
                v-for="d in WINDOW_OPTIONS"
                :key="d"
                class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left hover:bg-n-alpha-2"
                :class="d === windowDays ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'"
                @click="selectWindow(d)"
              >
                <span
                  class="i-lucide-check size-4 flex-shrink-0"
                  :class="d === windowDays ? 'text-n-slate-12' : 'opacity-0'"
                />
                <span class="whitespace-nowrap">Últimos {{ d }} dias</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </header>

    <!-- Barra de ações em massa: aparece quando há ≥1 card selecionado. -->
    <div
      v-if="selectedIds.size"
      class="flex flex-wrap items-center flex-shrink-0 gap-2 px-6 py-2 border-b bg-n-alpha-1 border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ selectedIds.size }} selecionado(s)
      </span>
      <span v-if="bulkRunning" class="text-xs text-n-slate-10">
        <span class="i-lucide-loader-circle size-3.5 animate-spin align-middle" />
        movendo {{ bulkInfo }}…
      </span>

      <div v-if="!bulkRunning" class="relative ml-auto">
        <button
          class="flex items-center gap-1.5 !h-8 px-3 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
          @click="bulkMenuOpen = !bulkMenuOpen"
        >
          <span class="i-lucide-move size-4" />
          Mover para
          <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
        </button>
        <template v-if="bulkMenuOpen">
          <div class="fixed inset-0 z-40" @click="bulkMenuOpen = false" />
          <div
            class="absolute right-0 z-50 py-1 mt-1 overflow-y-auto border shadow-md rounded-lg top-full min-w-[12rem] max-h-72 bg-n-background border-n-weak"
          >
            <button
              v-for="col in activeColumns"
              :key="col.id"
              class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left text-n-slate-11 hover:bg-n-alpha-2"
              @click="bulkMoveToStage(col)"
            >
              <span :class="[col.icon, col.accent, 'size-4 flex-shrink-0']" />
              <span class="truncate">{{ col.label }}</span>
            </button>
          </div>
        </template>
      </div>

      <button
        v-if="!bulkRunning && !currentFunnel.isStatus"
        class="flex items-center gap-1.5 !h-8 px-3 text-sm rounded-lg text-n-ruby-11 bg-n-alpha-2 hover:bg-n-alpha-3"
        @click="bulkRemove"
      >
        <span class="i-lucide-check-check size-4" />
        Remover/Finalizar
      </button>

      <button
        v-if="!bulkRunning"
        class="flex items-center gap-1.5 !h-8 px-3 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        @click="clearSelection"
      >
        <span class="i-lucide-x size-4" />
        Limpar
      </button>
    </div>

    <p
      v-if="!customFunnelList.length"
      class="flex-shrink-0 px-6 py-2 text-xs text-n-slate-10"
    >
      Nenhum funil ainda.
      <template v-if="isAdmin">Clique em "Novo" para criar um.</template>
      <template v-else
        >Peça a um administrador para criar um funil.</template
      >
      Cada etapa vira uma coluna.
    </p>

    <Draggable
      :list="activeColumns"
      :group="{ name: 'kanban-columns' }"
      item-key="id"
      tag="div"
      handle=".kanban-col-grip"
      :move="onColumnMove"
      class="flex flex-1 gap-4 p-4 overflow-x-auto"
      @end="onColumnReorder"
    >
      <template #item="{ element: col }">
        <section class="flex flex-col flex-shrink-0 w-80 rounded-xl bg-n-alpha-1">
          <div
            class="flex items-center justify-between flex-shrink-0 gap-2 px-3 py-3"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span
                v-if="canDragColumn(col)"
                class="kanban-col-grip i-lucide-grip-vertical size-4 flex-shrink-0 text-n-slate-9 cursor-grab active:cursor-grabbing"
                title="Arraste para reordenar a coluna"
              />
              <span :class="[col.icon, col.accent, 'size-4 flex-shrink-0']" />
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ col.label }}
              </span>
            </div>
            <div class="flex items-center flex-shrink-0 gap-1">
              <button
                class="p-1 rounded hover:text-n-slate-12 hover:bg-n-alpha-2"
                :class="
                  columnAllSelected(col.id) ? 'text-n-teal-11' : 'text-n-slate-9'
                "
                :title="
                  selectAllBusy === col.id
                    ? 'Carregando…'
                    : columnAllSelected(col.id)
                      ? 'Desmarcar todos desta etapa'
                      : 'Selecionar todos desta etapa (carrega todas as páginas)'
                "
                :disabled="selectAllBusy === col.id"
                @click="selectAllInColumn(col)"
              >
                <span
                  class="size-4"
                  :class="
                    selectAllBusy === col.id
                      ? 'i-lucide-loader-circle animate-spin'
                      : columnAllSelected(col.id)
                        ? 'i-lucide-check-square'
                        : 'i-lucide-square'
                  "
                />
              </button>
              <span
                class="px-2 py-0.5 text-xs rounded-full text-n-slate-11 bg-n-alpha-2"
              >
                {{ columnCountLabel(col.id) }}
              </span>
            </div>
          </div>

          <div
            v-col-scroll="col.id"
            class="flex flex-col flex-1 pl-2 pb-3 overflow-y-auto"
            :class="columnOverflow[col.id] ? 'pr-1' : 'pr-2'"
            @scroll="onColumnScroll(col, $event)"
          >
            <div
              v-if="boards[col.id] && boards[col.id].loading"
              class="flex items-center justify-center py-8 text-sm text-n-slate-10"
            >
              Carregando…
            </div>

            <Draggable
              v-else-if="boards[col.id]"
              :list="boards[col.id].items"
              :group="{ name: 'kanban-cards' }"
              item-key="id"
              tag="div"
              class="flex flex-col flex-1 gap-2 min-h-[3rem]"
              ghost-class="kanban-ghost"
              drag-class="kanban-drag"
              @change="onCardChange(col, $event)"
            >
              <template #item="{ element: conversation }">
                <div
                  class="relative flex flex-col w-full gap-2 p-3 text-left transition-colors border rounded-lg cursor-grab active:cursor-grabbing group bg-n-background border-n-weak hover:border-n-strong"
                  :class="{
                    'opacity-60': savingIds.has(conversation.id),
                    'ring-2 ring-n-teal-9 border-n-teal-9': isSelected(
                      conversation.id
                    ),
                  }"
                  @click="openConversation(conversation)"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <input
                      type="checkbox"
                      class="flex-shrink-0 transition-opacity cursor-pointer size-4 accent-n-teal-9"
                      :class="
                        isSelected(conversation.id)
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100'
                      "
                      :checked="isSelected(conversation.id)"
                      title="Selecionar"
                      @click.stop
                      @mousedown.stop
                      @change="toggleSelect(conversation)"
                    />
                    <Avatar
                      :name="senderName(conversation)"
                      :src="senderThumbnail(conversation)"
                      :size="28"
                    />
                    <span class="text-sm font-medium truncate text-n-slate-12">
                      {{ senderName(conversation) }}
                    </span>
                    <span class="ml-auto text-xs whitespace-nowrap text-n-slate-10">
                      #{{ conversation.id }}
                    </span>
                    <button
                      v-if="!currentFunnel.isStatus"
                      class="flex-shrink-0 p-0.5 rounded text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2"
                      title="Opções"
                      @click.stop="openCardMenu(conversation, $event)"
                      @mousedown.stop
                    >
                      <span class="i-lucide-more-vertical size-4" />
                    </button>
                  </div>

                  <p
                    v-if="lastMessageText(conversation)"
                    class="m-0 text-xs line-clamp-2 text-n-slate-11"
                  >
                    {{ lastMessageText(conversation) }}
                  </p>

                  <div
                    class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
                  >
                    <span
                      v-if="conversation.meta && conversation.meta.assignee"
                      class="flex items-center gap-1 truncate"
                    >
                      <Icon icon="i-lucide-user-round" class="size-3" />
                      {{ conversation.meta.assignee.name }}
                    </span>
                    <span v-else class="italic">Sem responsável</span>
                    <span class="flex-shrink-0">
                      {{ formatTimestamp(conversation) }}
                    </span>
                  </div>
                </div>
              </template>

              <template #footer>
                <div
                  v-if="boards[col.id] && !boards[col.id].items.length"
                  class="flex items-center justify-center py-8 text-sm pointer-events-none text-n-slate-10"
                >
                  Nenhuma conversa
                </div>
              </template>
            </Draggable>

            <!-- Scroll infinito: carrega a próxima página quando a rolagem DESTA
                 coluna chega perto do fim (onColumnScroll lê scrollTop/scrollHeight
                 do próprio container). Substituiu o IntersectionObserver, que com
                 root=viewport disparava em cascata e carregava tudo sem rolar. -->
            <div
              v-if="boards[col.id] && boards[col.id].loadingMore"
              class="flex items-center justify-center py-3 text-xs pointer-events-none text-n-slate-10"
            >
              Carregando mais…
            </div>
          </div>
        </section>
      </template>
    </Draggable>

    <!-- Menu do card (⋮): posicionado no clique (fixed), fora do overflow -->
    <template v-if="cardMenu">
      <div class="fixed inset-0 z-[60]" @click="cardMenu = null" />
      <div
        class="fixed z-[70] py-1 border shadow-md rounded-lg min-w-[12rem] bg-n-background border-n-weak"
        :style="{ top: `${cardMenu.y}px`, left: `${cardMenu.x}px` }"
      >
        <button
          class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left text-n-ruby-11 hover:bg-n-alpha-2"
          @click="removeFromFunnel(cardMenu.conversation)"
        >
          <span class="i-lucide-trash-2 size-4 flex-shrink-0" />
          Remover do funil
        </button>
      </div>
    </template>

    <!-- Popup: novo lead atribuído ao admin (bloqueante + som) -->
    <template v-if="assignPopup.open">
      <div class="fixed inset-0 z-[80] bg-n-alpha-black2" />
      <div
        class="fixed z-[90] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col w-full max-w-sm gap-4 p-5 border shadow-lg rounded-xl bg-n-background border-n-weak max-h-[85vh]"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-bell-ring size-5 flex-shrink-0 text-n-teal-11" />
          <h2 class="text-base font-medium text-n-slate-12">
            {{
              assignPopup.leads.length > 1
                ? 'Novos leads atribuídos a você'
                : 'Novo lead atribuído a você'
            }}
          </h2>
        </div>
        <p class="m-0 text-sm text-n-slate-11">
          Você recebeu {{ assignPopup.leads.length }}
          {{ assignPopup.leads.length > 1 ? 'leads' : 'lead' }} para resolver:
        </p>
        <ul class="flex flex-col gap-1 p-0 m-0 overflow-y-auto list-none max-h-60">
          <li v-for="lead in assignPopup.leads" :key="lead.id">
            <button
              class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm text-left border rounded-lg border-n-weak hover:border-n-strong hover:bg-n-alpha-2"
              @click="openAssignedLead(lead)"
            >
              <span class="truncate text-n-slate-12">
                {{ lead.name }}
                <span class="text-n-slate-10">#{{ lead.id }}</span>
              </span>
              <span class="flex-shrink-0 text-xs text-n-teal-11">Abrir</span>
            </button>
          </li>
        </ul>
        <div class="flex justify-end">
          <button
            class="flex items-center h-9 px-4 text-sm rounded-lg text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
            @click="dismissAssignPopup"
          >
            OK
          </button>
        </div>
      </div>
    </template>

    <!-- Editor de funil (admin) -->
    <div
      v-if="showEditor"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
    >
      <div
        class="flex flex-col w-full max-w-md gap-4 p-6 border shadow-lg rounded-xl bg-n-background border-n-weak max-h-[85vh]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ editorMode === 'create' ? 'Novo funil' : 'Gerenciar funil' }}
          </h2>
          <button
            class="grid rounded-lg size-8 shrink-0 place-content-center text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="closeEditor"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Nome do funil -->
        <label class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">Nome do funil</span>
          <input
            v-model="editorName"
            type="text"
            placeholder="Ex.: Funil de Vendas"
            class="w-full h-10 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-brand"
          />
        </label>

        <!-- Etapas -->
        <div class="flex flex-col min-h-0 gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">Etapas (colunas)</span>

          <div class="flex flex-col gap-2 pr-1 -mr-1 overflow-y-auto">
            <Draggable
              :list="editorStages"
              item-key="id"
              tag="div"
              handle=".stage-grip"
              class="flex flex-col gap-2"
            >
              <template #item="{ element: stage, index }">
                <div
                  class="flex items-center h-10 gap-2 px-3 border rounded-lg bg-n-alpha-1 border-n-weak"
                >
                  <span
                    class="stage-grip i-lucide-grip-vertical size-4 shrink-0 text-n-slate-9 cursor-grab active:cursor-grabbing"
                  />
                  <div class="relative flex items-center shrink-0">
                    <button
                      type="button"
                      class="block rounded-full size-4 ring-1 ring-inset ring-n-weak"
                      :style="dotStyleForColor(stage.color)"
                      title="Cor da coluna"
                      @click="toggleColorPicker(stage.id)"
                    />
                    <div
                      v-if="colorPickerRowId === stage.id"
                      class="absolute left-0 z-10 flex flex-wrap gap-1 p-1.5 mt-1 border rounded-lg shadow-md top-full bg-n-background border-n-weak w-[180px]"
                    >
                      <button
                        v-for="c in STAGE_COLORS"
                        :key="c.key"
                        type="button"
                        class="rounded-full size-5 ring-1 ring-inset ring-n-weak hover:ring-n-strong"
                        :style="{ backgroundColor: c.hex }"
                        :title="c.label"
                        @click="pickColor(stage, c.key)"
                      />
                    </div>
                  </div>
                  <input
                    v-model="stage.value"
                    type="text"
                    class="flex-1 min-w-0 text-sm leading-5 text-n-slate-12 !h-full !min-h-0 !m-0 !p-0 !bg-transparent !border-0 !shadow-none !outline-none"
                  />
                  <button
                    class="grid rounded size-6 shrink-0 place-content-center text-n-slate-10 hover:text-n-ruby-11"
                    title="Remover etapa"
                    @click="removeEditorStage(index)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </template>
            </Draggable>
          </div>

          <!-- Nova etapa -->
          <div class="flex items-center gap-2">
            <input
              v-model="editorNewStage"
              type="text"
              placeholder="Nova etapa"
              class="flex-1 h-10 min-w-0 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-brand !my-0"
              @keyup.enter="addEditorStage"
            />
            <button
              class="flex items-center h-10 gap-1 px-3 text-sm rounded-lg shrink-0 text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3"
              @click="addEditorStage"
            >
              <span class="i-lucide-plus size-4" />
              Adicionar
            </button>
          </div>

          <p
            v-if="editorMode === 'edit'"
            class="text-xs text-n-slate-10"
          >
            Renomear uma etapa move as conversas dela junto; remover envia as
            conversas dessa etapa para "Sem etapa".
          </p>
        </div>

        <!-- Status / erro -->
        <p
          v-if="migrationInfo"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <span class="i-lucide-refresh-cw size-3 animate-spin" />
          {{ migrationInfo }}
        </p>
        <p v-if="editorError" class="text-xs text-n-ruby-11">{{ editorError }}</p>

        <!-- Footer -->
        <div class="flex items-center justify-between gap-2">
          <button
            v-if="editorMode === 'edit'"
            class="flex items-center h-10 gap-1 px-3 text-sm rounded-lg text-n-ruby-11 hover:bg-n-alpha-2"
            :disabled="editorSaving"
            @click="deleteFunnel"
          >
            <span class="i-lucide-trash-2 size-4" />
            Excluir funil
          </button>
          <span v-else />

          <div class="flex items-center gap-2">
            <button
              class="h-10 px-4 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
              :disabled="editorSaving"
              @click="closeEditor"
            >
              Cancelar
            </button>
            <button
              class="flex items-center h-10 gap-1 px-4 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
              :disabled="editorSaving"
              @click="editorMode === 'create' ? saveCreate() : saveEdit()"
            >
              {{ editorSaving ? 'Salvando…' : 'Salvar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-ghost {
  opacity: 0.4;
}
.kanban-drag {
  cursor: grabbing;
}
</style>
